// Các phép tính trên cây tổ chức (phòng ban / nhân sự) — tách riêng để test và dùng lại khi có API.

export interface RawNode {
  id: string;
  type: 'department' | 'employee';
  label?: string;
  count?: number;
  badgeColor?: string;
  name?: string;
  role?: string;
  avatar?: string;
  children?: RawNode[];
}

export interface EmployeeRef { node: RawNode; path: string[] }

const norm = (s: string) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase().trim();

/** Tổng số nhân sự trong cả nhánh (kể cả phòng ban con) */
export function countEmployees(node: RawNode): number {
  if (node.type === 'employee') return 1 + (node.children || []).reduce((s, c) => s + countEmployees(c), 0);
  return (node.children || []).reduce((s, c) => s + countEmployees(c), 0);
}

/** Mọi nhân sự trong nhánh kèm đường dẫn phòng ban (để hiện "Ban Giám Đốc › Phòng BA") */
export function collectEmployees(node: RawNode, path: string[] = []): EmployeeRef[] {
  const here = node.type === 'department' ? [...path, node.label || ''] : path;
  const self = node.type === 'employee' ? [{ node, path }] : [];
  return [...self, ...(node.children || []).flatMap(c => collectEmployees(c, here))];
}

/** Chức danh quản lý: trưởng / phó / giám đốc / quản lý / leader */
export const isLeader = (role?: string) => /\btruong\b|\bpho\b|giam doc|quan ly|\bleader\b|\blead\b|\bhead\b|manager|director|\btong\b/.test(norm(role || ''));

/** Chỉ giữ nhân sự là quản lý; phòng ban không còn ai thì bỏ khỏi cây (trừ gốc) */
export function filterLeaders(node: RawNode, isRoot = true): RawNode | null {
  if (node.type === 'employee') return isLeader(node.role) ? { ...node, children: undefined } : null;
  const children = (node.children || []).map(c => filterLeaders(c, false)).filter((c): c is RawNode => !!c);
  if (!children.length && !isRoot) return null;
  return { ...node, children };
}

/** Cấp chức vụ để nhóm "Theo chức vụ" */
export function roleLevel(role?: string): 0 | 1 | 2 {
  const r = norm(role || '');
  if (/giam doc|tong|director|ceo|chu tich/.test(r)) return 0;
  if (isLeader(role)) return 1;
  return 2;
}
export const LEVEL_LABEL = ['Ban lãnh đạo', 'Quản lý', 'Nhân viên'] as const;
const LEVEL_COLOR = ['bg-amber-500', 'bg-blue-500', 'bg-emerald-500'];

/** Cây theo chức vụ: gốc → 3 cấp (lãnh đạo / quản lý / nhân viên) → nhân sự (chức danh kèm phòng ban) */
export function buildPositionTree(root: RawNode, companyLabel: string): RawNode {
  const groups: RawNode[][] = [[], [], []];
  collectEmployees(root).forEach(({ node, path }) => {
    const dept = path[path.length - 1];
    groups[roleLevel(node.role)].push({ ...node, id: `pos-${node.id}`, role: [node.role, dept].filter(Boolean).join(' · '), children: undefined });
  });
  return {
    id: 'pos-root', type: 'department', label: companyLabel, badgeColor: 'bg-amber-500',
    children: groups
      .map((list, i) => ({ id: `pos-level-${i}`, type: 'department' as const, label: LEVEL_LABEL[i], badgeColor: LEVEL_COLOR[i], children: list }))
      .filter(g => g.children.length),
  };
}

export interface SearchHit { id: string; kind: 'department' | 'employee'; label: string; sub: string; depth: number }

/** Tìm phòng ban và nhân sự (không dấu); rỗng = liệt kê phòng ban theo cây */
export function searchOrg(root: RawNode, q: string): SearchHit[] {
  const k = norm(q);
  const out: SearchHit[] = [];
  const walk = (n: RawNode, depth: number, path: string[]) => {
    if (n.type === 'department') {
      if (!k || norm(n.label || '').includes(k)) out.push({ id: n.id, kind: 'department', label: n.label || '', sub: `${countEmployees(n)} nhân sự`, depth });
      (n.children || []).forEach(c => walk(c, depth + 1, [...path, n.label || '']));
    } else {
      if (k && (norm(n.name || '').includes(k) || norm(n.role || '').includes(k))) {
        out.push({ id: n.id, kind: 'employee', label: n.name || '', sub: [n.role, path[path.length - 1]].filter(Boolean).join(' · '), depth });
      }
      (n.children || []).forEach(c => walk(c, depth + 1, path));
    }
  };
  walk(root, 0, []);
  return out;
}

export function getAncestorsAndSelf(root: RawNode, targetId: string, path: string[] = []): string[] | null {
  const currentPath = [...path, root.id];
  if (root.id === targetId) return currentPath;
  for (const child of root.children || []) {
    const res = getAncestorsAndSelf(child, targetId, currentPath);
    if (res) return res;
  }
  return null;
}

export function findNode(root: RawNode, id: string): RawNode | null {
  if (root.id === id) return root;
  for (const c of root.children || []) { const f = findNode(c, id); if (f) return f; }
  return null;
}
